import { useUserContext } from '../hooks';
import { amplitudeExperiment } from './AmplitudeExperimentSDK';
import { useEffect } from 'react';

/**
 * @description Use for percentage-based feature rollouts, with flags set up in Amplitude.
 * @param {string} flagKey - The flag key
 * @param {Object.<string, any>} [audienceAttributes]  (optional) - attributes that identify the current user.
 * @returns {boolean} True if a user is included in the feature flag, false otherwise.
 * @example <caption>Check if feature is enabled for a team with specific audience attributes</caption>
 * const isFeatureEnabled = useFeatureEnabledRollout({ flagKey: 'grow-777_example_flag', audienceAttributes: { has_droplets: true } });
 **/

interface FeatureEnabledRolloutProps {
  flagKey: string;
  audienceAttributes?: { [name: string]: any };
}

export const useFeatureEnabledRollout = ({
  flagKey,
  audienceAttributes
}: FeatureEnabledRolloutProps): boolean => {
  const { user } = useUserContext();

  const amplitudeFlag = amplitudeExperiment.variant(flagKey);

  const isFeatureEnabled = amplitudeFlag.value === 'on';

  useEffect(() => {
    const fetchAmplitudeFlagWithAudience = async (): Promise<void> => {
      await amplitudeExperiment.fetch({
        user_id: user.current_context_urn,
        user_properties: audienceAttributes
      });
    };

    fetchAmplitudeFlagWithAudience();
  }, []);

  return isFeatureEnabled;
};
